/**
 * Profile bubble color utilities
 * Must match iOS implementation exactly for cross-platform consistency
 */

/**
 * 15 profile bubble colors with translucent appearance (70% opacity)
 * EXACT RGB values from iOS - must match ProfileColors.swift
 */
export const bubbleColors = [
  'rgba(250, 115, 115, 0.7)',  // iOS: Color(red: 0.98, green: 0.45, blue: 0.45)
  'rgba(250, 191, 77, 0.7)',   // iOS: Color(red: 0.98, green: 0.75, blue: 0.30)
  'rgba(166, 230, 89, 0.7)',   // iOS: Color(red: 0.65, green: 0.90, blue: 0.35)
  'rgba(71, 212, 166, 0.7)',   // iOS: Color(red: 0.28, green: 0.83, blue: 0.65)
  'rgba(33, 212, 230, 0.7)',   // iOS: Color(red: 0.13, green: 0.83, blue: 0.90)
  'rgba(56, 179, 242, 0.7)',   // iOS: Color(red: 0.22, green: 0.70, blue: 0.95)
  'rgba(97, 140, 250, 0.7)',   // iOS: Color(red: 0.38, green: 0.55, blue: 0.98)
  'rgba(128, 115, 250, 0.7)',  // iOS: Color(red: 0.50, green: 0.45, blue: 0.98)
  'rgba(166, 115, 250, 0.7)',  // iOS: Color(red: 0.65, green: 0.45, blue: 0.98)
  'rgba(191, 107, 235, 0.7)',  // iOS: Color(red: 0.75, green: 0.42, blue: 0.92)
  'rgba(230, 102, 217, 0.7)',  // iOS: Color(red: 0.90, green: 0.40, blue: 0.85)
  'rgba(242, 115, 166, 0.7)',  // iOS: Color(red: 0.95, green: 0.45, blue: 0.65)
  'rgba(250, 148, 71, 0.7)',   // iOS: Color(red: 0.98, green: 0.58, blue: 0.28)
  'rgba(46, 191, 184, 0.7)',   // iOS: Color(red: 0.18, green: 0.75, blue: 0.72)
  'rgba(77, 204, 122, 0.7)',   // iOS: Color(red: 0.30, green: 0.80, blue: 0.48)
];

/**
 * Hash function for consistent color assignment by name
 * Normalizes to lowercase for case-insensitive matching
 * Must match the iOS implementation exactly
 *
 * iOS uses: hash = ((hash << 5) &- hash) &+ char
 * Which is (hash * 31 + char) with overflow wrapping
 */
export function hashName(name: string): number {
  const normalized = name.toLowerCase().trim();
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized.charCodeAt(i);
    // (hash << 5) - hash = hash * 31
    // Use |0 to convert to 32-bit integer (simulates overflow like iOS)
    hash = (((hash << 5) - hash) + char) | 0;
  }
  return Math.abs(hash);
}

/**
 * Get a consistent Tailwind color class for a given name
 */
export function getColorForName(name: string): string {
  return bubbleColors[hashName(name) % bubbleColors.length];
}

/**
 * Get initials from a name (first letter of first and last name, or first two characters)
 */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}
